import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { firebaseConfig } from '../config/firebase';

const PROVISIONING_APP_NAME = 'AdminUserProvisioning';

const getProvisioningAuth = () => {
  const existingApp = getApps().find((app) => app.name === PROVISIONING_APP_NAME);
  const app = existingApp ?? initializeApp(firebaseConfig, PROVISIONING_APP_NAME);
  return getAuth(app);
};

const mapProvisioningErrorMessage = (error) => {
  const code = error?.code;

  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email already has a Firebase account, but the password did not match. Use the correct password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/operation-not-allowed':
      return 'Email/password sign-in is not enabled for this Firebase project.';
    default:
      return error?.message || 'Failed to provision Firebase user';
  }
};

/**
 * Resolves a Firebase Auth uid for partner/admin provisioning without disrupting
 * the signed-in admin session (uses a secondary Firebase app).
 *
 * Flow: try signInWithEmailAndPassword → on failure createUserWithEmailAndPassword.
 * Existing accounts with matching password reuse the uid; new emails create a user.
 */
export const createFirebaseUser = async (email, password) => {
  const provisioningAuth = getProvisioningAuth();
  const normalizedEmail = String(email ?? '').trim();

  try {
    try {
      const existingCredential = await signInWithEmailAndPassword(
        provisioningAuth,
        normalizedEmail,
        password,
      );
      const uid = existingCredential.user.uid;
      await signOut(provisioningAuth);
      return { success: true, uid, created: false };
    } catch {
      // Account missing or credentials rejected — attempt create next.
    }

    const credential = await createUserWithEmailAndPassword(
      provisioningAuth,
      normalizedEmail,
      password,
    );
    const uid = credential.user.uid;
    await signOut(provisioningAuth);
    return { success: true, uid, created: true };
  } catch (error) {
    await signOut(provisioningAuth).catch(() => {});
    return {
      success: false,
      message: mapProvisioningErrorMessage(error),
      code: error?.code ?? null,
    };
  }
};
